import { useAuth } from "@/contexts/AuthContext";
import {
  avaliarAvisoSync,
  consumirPedidoSincronizacao,
  marcarAvisoSyncVisto,
  observarAvisoSync,
  observarPedidoSincronizacao,
  sincronizarAgora,
  type ResultadoSincronizacao,
  type SituacaoAvisoSync,
} from "@/services/AvisoSync";
import { SyncService } from "@/services/Sync";
import {
  descreverPendenciasSync,
  mensagemResultadoSync,
  textoAtrasoSync,
  type NivelAvisoSync,
} from "@/utils/avisoSync";
import { donoDosExtras } from "@/utils/pontosExtras";
import { mostrarPushAvisoSync, sincronizarComPush } from "./pushAvisoSync";
import { MaterialIcons } from "@/ui/icons";
import { usePathname } from "@/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  AppState,
  BackHandler,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "@/ui/rn";
import { useSafeAreaInsets } from "@/ui/safe-area";
import { T, TJ } from "@/utils/i18n";

const PRIMARY = "#294956";
const ESCURO = "#122c36";
const SECONDARY = "#526f7b";
const VERDE = "#1e8c5a";
const AMBAR = "#b7791f";

/** Reavaliação periódica com o app aberto. */
const INTERVALO_MS = 60 * 1000;
/** O aviso curto (nível 1, só sem permissão de notificação) some sozinho. */
const BANNER_SOME_MS = 20 * 1000;

// Rotas que o aviso NÃO cobre: entrada/login, câmera de registro e biometria. Ele espera a
// pessoa sair delas (o aviso continua pendente).
const ROTAS_SEM_AVISO = new Set([
  "/",
  "/intro",
  "/permissions",
  "/onboarding",
  "/onboarding-prestador",
  "/environment-select",
  "/camera",
  "/camera-scan",
  "/camera-diaria",
  "/biometric-setup",
  "/biometria",
  "/registro-diaria",
]);

/**
 * Aviso de SINCRONIZAÇÃO PENDENTE (regra em utils/avisoSync, motor em services/AvisoSync):
 *  - ≥ 30 min: notificação local (agendada pelo motor; aqui só o aviso de reserva quando
 *    não há permissão de notificação);
 *  - > 1 h: diálogo no estilo do informativo da Home;
 *  - > 8 h: tela cheia.
 * Todos com "Sincronizar agora" e o resultado logo abaixo.
 *
 * Montado no _layout.tsx raiz, por cima da navegação e POR BAIXO dos Modals nativos: um
 * pop-up obrigatório (enquete, assinatura, atualização do app) continua na frente, e o
 * aviso aparece quando ele fecha — sem dois Modals ao mesmo tempo no iOS.
 */
export default function AvisoSync() {
  const { isAuthenticated, userData } = useAuth();
  const pathname = usePathname();
  const insets = useSafeAreaInsets();
  const logado = isAuthenticated && !!userData;
  const dono = logado ? donoDosExtras(userData) : "";

  const [s, setS] = useState<SituacaoAvisoSync | null>(null);
  const [aberto, setAberto] = useState<NivelAvisoSync>(0);
  const [resultado, setResultado] = useState<ResultadoSincronizacao | null>(null);
  const entrada = useRef(new Animated.Value(0)).current;
  // Conteúdo do aviso = a última leitura COM pendência: no "Tudo sincronizado" (antes de
  // fechar) o texto não pode virar "0 registros… há 0 minutos".
  const comPendencia = useRef<SituacaoAvisoSync | null>(null);
  if (s && s.total > 0) comPendencia.current = s;

  useEffect(() => observarAvisoSync(setS), []);

  // Reavalia ao entrar, a cada minuto com o app aberto, ao voltar ao primeiro plano, ao ir
  // para o fundo (deixa a notificação agendada antes de o sistema congelar o app) e quando
  // as batidas locais mudam (batida nova, enviada).
  useEffect(() => {
    if (!dono) return;
    const avaliar = () => {
      void avaliarAvisoSync(dono).catch(() => {});
    };
    avaliar();
    let timer: ReturnType<typeof setInterval> | null = setInterval(avaliar, INTERVALO_MS);
    const subApp = AppState.addEventListener("change", (estado) => {
      avaliar();
      if (estado === "active") {
        if (!timer) timer = setInterval(avaliar, INTERVALO_MS);
      } else if (timer) {
        clearInterval(timer);
        timer = null;
      }
    });
    const pararPontos = SyncService.observarPontosLocais(avaliar);
    return () => {
      if (timer) clearInterval(timer);
      subApp.remove();
      pararPontos();
    };
  }, [dono]);

  const livre = logado && !ROTAS_SEM_AVISO.has(pathname || "/");

  // Abre o que a avaliação pedir.
  useEffect(() => {
    // WEB: o nível 1 é o push (efeito abaixo), não o aviso no topo do app.
    if (!s || !livre || aberto || s.exibir === 0 || s.exibir === 1) return;
    setResultado(null);
    setAberto(s.exibir);
    void marcarAvisoSyncVisto(s.exibir);
  }, [s, livre, aberto]);

  // WEB: nível 1 = push na tela. Só com a aba visível — oculta, quem avisa é a notificação
  // do sistema (services/AvisoSync), e o push esperaria a volta já vencido.
  useEffect(() => {
    if (!s || !livre || s.exibir !== 1 || document.visibilityState === "hidden") return;
    mostrarPushAvisoSync(s);
    void marcarAvisoSyncVisto(1);
  }, [s, livre]);

  // Enviado por outro caminho (envio automático): fecha.
  useEffect(() => {
    if (aberto && s && s.total === 0 && !resultado) setAberto(0);
  }, [aberto, s, resultado]);

  useEffect(() => {
    if (!aberto) return;
    entrada.setValue(0);
    Animated.timing(entrada, { toValue: 1, duration: 220, useNativeDriver: true }).start();
  }, [aberto, entrada]);

  const fechar = useCallback(() => {
    setAberto(0);
    setResultado(null);
  }, []);

  const sincronizar = useCallback(async () => {
    setResultado(null);
    const r = await sincronizarAgora();
    setResultado(r);
    if (r.restantes === 0) setTimeout(fechar, 1800);
  }, [fechar]);

  // Toque na notificação ("Sincronizar agora" ou o corpo): com sessão, sincroniza. Com um
  // aviso aberto o resultado aparece nele; sem aviso, num toast.
  const abertoRef = useRef<NivelAvisoSync>(0);
  abertoRef.current = aberto;
  useEffect(() => {
    if (!dono) return;
    return observarPedidoSincronizacao(() => {
      if (!consumirPedidoSincronizacao()) return;
      if (abertoRef.current) {
        void sincronizar();
        return;
      }
      // WEB: andamento e resultado no push (no app nativo, toast).
      sincronizarComPush();
    });
  }, [dono, sincronizar]);

  // Android: voltar fecha o aviso ("lembrar mais tarde" na tela cheia).
  useEffect(() => {
    if (!aberto || !livre) return;
    const sub = BackHandler.addEventListener("hardwareBackPress", () => {
      fechar();
      return true;
    });
    return () => sub.remove();
  }, [aberto, livre, fechar]);

  // O aviso curto some sozinho (se ninguém estiver sincronizando por ele).
  const sincronizando = !!s?.sincronizando;
  useEffect(() => {
    if (aberto !== 1 || sincronizando || resultado) return;
    const t = setTimeout(fechar, BANNER_SOME_MS);
    return () => clearTimeout(t);
  }, [aberto, sincronizando, resultado, fechar]);

  // Rota que não pode ser coberta (câmera…): esconde e volta quando a pessoa sair dela.
  if (!aberto || !s || !livre) return null;

  const v = comPendencia.current ?? s;
  const itens = descreverPendenciasSync(v.contagem);
  const atraso = textoAtrasoSync(v.idadeMs);
  const msg = resultado ? mensagemResultadoSync(resultado) : null;
  const total = v.total;
  const totalTexto = `${total} ${total === 1 ? T("registro") : T("registros")}`;

  const caixaResultado = (escuro: boolean) =>
    msg ? (
      <View
        style={[
          styles.resultado,
          escuro && styles.resultadoEscuro,
          { borderColor: msg.ok ? VERDE : AMBAR },
        ]}
      >
        <MaterialIcons
          name={msg.ok ? "check-circle" : resultado?.semInternet ? "wifi-off" : "error-outline"}
          size={20}
          color={msg.ok ? VERDE : AMBAR}
        />
        <View style={{ flex: 1 }}>
          <Text style={[styles.resultadoTitulo, escuro && { color: "#fff" }]}>{msg.titulo}</Text>
          <Text style={[styles.resultadoTexto, escuro && { color: "#cfe0e6" }]}>
            {msg.texto}
          </Text>
        </View>
      </View>
    ) : null;

  const botaoSincronizar = (claro: boolean) => (
    <Pressable
      onPress={sincronizar}
      disabled={sincronizando}
      style={({ pressed }) => [
        styles.botao,
        claro ? styles.botaoClaro : styles.botaoPrimario,
        (pressed || sincronizando) && { opacity: 0.85 },
      ]}
    >
      {sincronizando ? (
        <ActivityIndicator color={claro ? PRIMARY : "#fff"} />
      ) : (
        <MaterialIcons name="sync" size={20} color={claro ? PRIMARY : "#fff"} />
      )}
      <Text style={[styles.botaoTexto, claro && { color: PRIMARY }]}>
        {sincronizando ? T("Sincronizando...") : T("Sincronizar agora")}
      </Text>
    </Pressable>
  );

  // ── nível 3: tela cheia ──────────────────────────────────────────────────
  if (aberto === 3) {
    return (
      <Animated.View
        style={[
          styles.telaCheia,
          { opacity: entrada, paddingTop: insets.top + 16, paddingBottom: insets.bottom + 16 },
        ]}
      >
        <ScrollView contentContainerStyle={styles.tcConteudo} bounces={false}>
          <View style={styles.tcAnel}>
            <View style={styles.tcIcone}>
              <MaterialIcons name="cloud-off" size={44} color="#fff" />
            </View>
          </View>
          <Text style={styles.tcSelo}>{T("SINCRONIZAÇÃO PENDENTE")}</Text>
          <Text style={styles.tcTitulo}>
            {T("Seus registros estão há mais de 8 horas sem sincronizar")}
          </Text>
          <Text style={styles.tcTexto}>
            {TJ("{0} guardados só neste aparelho — o mais antigo foi guardado {1}. Até serem enviados, eles não chegam ao sistema da empresa.", [totalTexto, atraso])}
          </Text>
          {itens.length > 0 ? (
            <View style={styles.tcCartao}>
              {itens.map((t) => (
                <View key={t} style={styles.tcItem}>
                  <MaterialIcons name="schedule" size={16} color="#9fc3cf" />
                  <Text style={styles.tcItemTexto}>{t}</Text>
                </View>
              ))}
            </View>
          ) : null}
          <View style={styles.tcDica}>
            <MaterialIcons name="wifi" size={18} color="#9fc3cf" />
            <Text style={styles.tcDicaTexto}>
              {T("Conecte-se a uma rede Wi-Fi ou ative os dados móveis. Não desinstale o app nem apague os dados dele antes de sincronizar.")}
            </Text>
          </View>
          {caixaResultado(true)}
        </ScrollView>
        <View style={styles.tcRodape}>
          {botaoSincronizar(true)}
          <Pressable onPress={fechar} hitSlop={8} style={styles.tcDepois}>
            <Text style={styles.tcDepoisTexto}>{T("Lembrar mais tarde")}</Text>
          </Pressable>
        </View>
      </Animated.View>
    );
  }

  // ── nível 2: diálogo (estilo do informativo) ────────────────────────────
  if (aberto === 2) {
    return (
      <Animated.View style={[styles.fundo, { opacity: entrada }]}>
        <Animated.View
          style={[
            styles.cartao,
            {
              transform: [
                {
                  scale: entrada.interpolate({ inputRange: [0, 1], outputRange: [0.94, 1] }),
                },
              ],
            },
          ]}
        >
          <View style={styles.banner}>
            <View style={[styles.bolha, { width: 150, height: 150, top: -60, right: -40 }]} />
            <View style={[styles.bolha, { width: 90, height: 90, bottom: -40, left: -20 }]} />
            <View style={styles.bannerIcone}>
              <MaterialIcons name="cloud-upload" size={34} color="#fff" />
            </View>
            <View style={styles.bannerSelo}>
              <Text style={styles.bannerSeloTexto}>{T("INFORMATIVO")}</Text>
            </View>
            <Pressable onPress={fechar} hitSlop={10} style={styles.fecharBanner}>
              <MaterialIcons name="close" size={20} color="#fff" />
            </Pressable>
          </View>
          <ScrollView style={{ maxHeight: 300 }} contentContainerStyle={styles.cartaoCorpo}>
            <Text style={styles.titulo}>{T("Você tem registros sem sincronizar")}</Text>
            <Text style={styles.texto}>
              {TJ("{0} guardados neste aparelho ainda não chegaram ao sistema. O mais antigo foi guardado {1}.", [totalTexto, atraso])}
            </Text>
            {itens.map((t) => (
              <View key={t} style={styles.item}>
                <View style={styles.itemPonto} />
                <Text style={styles.itemTexto}>{t}</Text>
              </View>
            ))}
            <Text style={styles.dica}>
              {T("Conecte-se à internet e toque em \"Sincronizar agora\".")}
            </Text>
          </ScrollView>
          <View style={styles.cartaoRodape}>
            {/* Resultado no rodapé: dentro da rolagem ele caía fora da vista. */}
            {caixaResultado(false)}
            {botaoSincronizar(false)}
            <Pressable
              onPress={fechar}
              style={({ pressed }) => [styles.botao, styles.botaoContorno, pressed && { opacity: 0.85 }]}
            >
              <Text style={[styles.botaoTexto, { color: SECONDARY }]}>{T("Depois")}</Text>
            </Pressable>
          </View>
        </Animated.View>
      </Animated.View>
    );
  }

  // ── nível 1 sem permissão de notificação: aviso curto no topo ────────────
  return (
    <View style={[styles.bannerArea, { paddingTop: insets.top + 8 }]} pointerEvents="box-none">
      <Animated.View
        style={[
          styles.aviso,
          {
            opacity: entrada,
            transform: [
              {
                translateY: entrada.interpolate({ inputRange: [0, 1], outputRange: [-24, 0] }),
              },
            ],
          },
        ]}
      >
        <View style={styles.avisoTopo}>
          <View style={styles.avisoIcone}>
            <MaterialIcons name="cloud-upload" size={20} color="#fff" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.avisoTitulo}>{T("Itens aguardando sincronização")}</Text>
            <Text style={styles.avisoTexto}>
              {TJ("{0} guardados no aparelho {1}.", [totalTexto, atraso])}
            </Text>
          </View>
          <Pressable onPress={fechar} hitSlop={10}>
            <MaterialIcons name="close" size={20} color={SECONDARY} />
          </Pressable>
        </View>
        {caixaResultado(false)}
        {botaoSincronizar(false)}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  // tela cheia
  telaCheia: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: ESCURO,
    // WEB: preso à janela, abaixo das folhas (1000+) e dos Modals (1100+) — como no app,
    // em que eles são nativos e ficam na frente.
    position: "fixed",
    zIndex: 990,
    elevation: 50,
  },
  tcConteudo: {
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
    paddingVertical: 12,
    // WEB: no desktop o texto não se espalha pela tela larga (no celular não muda nada).
    width: "100%",
    maxWidth: 560,
    alignSelf: "center",
  },
  tcAnel: {
    width: 116,
    height: 116,
    borderRadius: 58,
    backgroundColor: "rgba(255,255,255,0.08)",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 18,
  },
  tcIcone: {
    width: 84,
    height: 84,
    borderRadius: 42,
    backgroundColor: "#c0392b",
    alignItems: "center",
    justifyContent: "center",
  },
  tcSelo: {
    color: "#f5b7a8",
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 1.4,
    marginBottom: 8,
  },
  tcTitulo: {
    color: "#fff",
    fontSize: 22,
    lineHeight: 28,
    fontWeight: "800",
    textAlign: "center",
  },
  tcTexto: {
    color: "#cfe0e6",
    fontSize: 14,
    lineHeight: 21,
    textAlign: "center",
    marginTop: 10,
  },
  tcCartao: {
    alignSelf: "stretch",
    backgroundColor: "rgba(255,255,255,0.07)",
    borderRadius: 14,
    padding: 14,
    gap: 8,
    marginTop: 18,
  },
  tcItem: { flexDirection: "row", alignItems: "center", gap: 8 },
  tcItemTexto: { color: "#fff", fontSize: 14, fontWeight: "600" },
  tcDica: {
    alignSelf: "stretch",
    flexDirection: "row",
    gap: 10,
    marginTop: 14,
    alignItems: "flex-start",
  },
  tcDicaTexto: { flex: 1, color: "#9fc3cf", fontSize: 12.5, lineHeight: 18 },
  // WEB: mesma coluna do conteúdo (ver tcConteudo).
  tcRodape: { paddingHorizontal: 24, paddingTop: 8, width: "100%", maxWidth: 560, alignSelf: "center" },
  tcDepois: { alignSelf: "center", paddingVertical: 14 },
  tcDepoisTexto: { color: "#cfe0e6", fontSize: 14, fontWeight: "700" },

  // diálogo (informativo)
  fundo: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(12,24,31,0.7)",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    // WEB: ver telaCheia.
    position: "fixed",
    zIndex: 990,
    elevation: 50,
  },
  cartao: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#fff",
    borderRadius: 22,
    overflow: "hidden",
  },
  banner: {
    height: 120,
    backgroundColor: PRIMARY,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  bolha: {
    position: "absolute",
    borderRadius: 999,
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  bannerIcone: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: "rgba(255,255,255,0.16)",
    alignItems: "center",
    justifyContent: "center",
  },
  bannerSelo: {
    position: "absolute",
    top: 14,
    left: 16,
    backgroundColor: "rgba(255,255,255,0.18)",
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  bannerSeloTexto: { color: "#fff", fontSize: 10, fontWeight: "800", letterSpacing: 1 },
  fecharBanner: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: "rgba(15,30,38,0.45)",
    alignItems: "center",
    justifyContent: "center",
  },
  cartaoCorpo: { padding: 20, paddingBottom: 12, gap: 10 },
  cartaoRodape: { paddingHorizontal: 20, paddingBottom: 20, gap: 10 },
  titulo: { fontSize: 18, fontWeight: "800", color: PRIMARY },
  texto: { fontSize: 14, lineHeight: 21, color: "#3a4a52" },
  item: { flexDirection: "row", alignItems: "center", gap: 8 },
  itemPonto: { width: 7, height: 7, borderRadius: 4, backgroundColor: PRIMARY },
  itemTexto: { fontSize: 14, color: PRIMARY, fontWeight: "700" },
  dica: { fontSize: 12.5, color: SECONDARY, marginTop: 2 },

  // aviso curto (nível 1 sem notificação)
  bannerArea: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 12,
    zIndex: 50,
    elevation: 50,
  },
  aviso: {
    backgroundColor: "#fff",
    borderRadius: 18,
    padding: 14,
    gap: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 18,
    elevation: 12,
  },
  avisoTopo: { flexDirection: "row", alignItems: "center", gap: 12 },
  avisoIcone: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: PRIMARY,
    alignItems: "center",
    justifyContent: "center",
  },
  avisoTitulo: { fontSize: 14, fontWeight: "800", color: PRIMARY },
  avisoTexto: { fontSize: 12.5, color: SECONDARY, marginTop: 1 },

  // comuns
  botao: {
    height: 50,
    borderRadius: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  botaoPrimario: { backgroundColor: PRIMARY },
  botaoClaro: { backgroundColor: "#fff" },
  botaoContorno: { backgroundColor: "#fff", borderWidth: 1, borderColor: "#e1e9ed" },
  botaoTexto: { color: "#fff", fontWeight: "800", fontSize: 15 },
  resultado: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    backgroundColor: "#fbfdfe",
    alignSelf: "stretch",
  },
  resultadoEscuro: { backgroundColor: "rgba(255,255,255,0.06)", marginTop: 16 },
  resultadoTitulo: { fontSize: 13.5, fontWeight: "800", color: PRIMARY },
  resultadoTexto: { fontSize: 12.5, lineHeight: 18, color: SECONDARY, marginTop: 2 },
});
